import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, DollarSign, Award, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AnalyticsDashboard() {
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [enrollmentsTrend, setEnrollmentsTrend] = useState([]);
  const [topSports, setTopSports] = useState([]);
  const [coachLeaderboard, setCoachLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  
  useEffect(() => {
    fetchAnalytics();
  }, [days]);
  
  const fetchAnalytics = async () => {
    try {
      const [revenueRes, enrollmentsRes, sportsRes, coachesRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/revenue-trend?days=${days}`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/enrollments-trend?days=${days}`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/top-sports`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/coach-leaderboard`, { withCredentials: true })
      ]);
      
      setRevenueTrend(revenueRes.data);
      setEnrollmentsTrend(enrollmentsRes.data);
      setTopSports(sportsRes.data);
      setCoachLeaderboard(coachesRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-deep-dark py-12" data-testid="analytics-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
          <div>
            <span className="inline-block px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-4">
              <BarChart3 className="w-4 h-4 inline mr-1" /> Business Intelligence
            </span>
            <h1 
              className="text-4xl md:text-5xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Platform <span className="gold-gradient">Analytics</span>
            </h1>
          </div>
          
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="px-4 py-3 bg-dark-surface border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        
        {/* Revenue Trend */}
        <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
            <TrendingUp className="w-6 h-6 text-emerald-400" strokeWidth={1.5} />
            Revenue Trend
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#C9A84C" />
              <XAxis dataKey="date" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1A0A2E', border: '1px solid #C9A84C', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={3} name="Revenue (₹)" dot={{ fill: '#10B981' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Enrollments Trend */}
        <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
            <Users className="w-6 h-6 text-premium-gold" strokeWidth={1.5} />
            Enrollments Over Time
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={enrollmentsTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#C9A84C" />
              <XAxis dataKey="date" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1A0A2E', border: '1px solid #C9A84C', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend />
              <Area type="monotone" dataKey="enrollments" stroke="#C9A84C" fill="#C9A84C" fillOpacity={0.3} name="Enrollments" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Top Sports */}
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50">
            <h2 className="text-2xl font-bold mb-4 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
              Top Sports by Enrollments
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topSports}>
                <CartesianGrid strokeDasharray="3 3" stroke="#C9A84C" />
                <XAxis dataKey="sport" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1A0A2E', border: '1px solid #C9A84C', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="enrollments" fill="#C9A84C" name="Enrollments" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Coach Leaderboard */}
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50">
            <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
              <Award className="w-6 h-6 text-premium-gold" strokeWidth={1.5} />
              Top Coaches
            </h2>
            <div className="space-y-4">
              {coachLeaderboard.map((coach, index) => (
                <div key={coach.user_id} className="flex items-center gap-4 p-3 bg-deep-dark/50 rounded-lg border border-premium-gold/30/30 hover:border-premium-gold/30 transition-colors">
                  <div className={`w-10 h-10 flex items-center justify-center font-black rounded-lg ${
                    index === 0 ? 'bg-premium-gold text-dark-purple' : 
                    index === 1 ? 'bg-gray-400 text-dark-purple' : 
                    index === 2 ? 'bg-amber-700 text-white' : 'bg-dark-surface text-white'
                  }`}>
                    {index + 1}
                  </div>
                  {coach.picture ? (
                    <img src={coach.picture} alt={coach.name} className="w-12 h-12 rounded-full border-2 border-premium-gold/30" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-deep-dark border-2 border-premium-gold/30"></div>
                  )}
                  <div className="flex-1">
                    <p className="font-bold text-white">{coach.name}</p>
                    <div className="flex gap-4 text-sm text-white/60">
                      <span>{coach.total_students} students</span>
                      <span className="text-emerald-400">₹{coach.total_revenue?.toFixed(0) || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboard;
